/**
 * This module is not intended for general use. Instead, use
 * auto-generated client stubs from the `@mc/autolyse/` directory.
 */

import { createClient } from '@mc/networking/http';
import postMessageHub from '@mc/post-message-hub';
import { createCustomerInteraction } from '@mc/appfabric/interactions';

const autolyseClient = createClient({
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

const ROOT = __DEV__ ? '/autolyse/dev.php/twirp' : '/autolyse/index.php/twirp';

type CustomerInteraction = {
  success: () => void;
  fail: (errorMsg: string) => void;
  abort: (reason?: string) => void;
  setDegraded: (reason?: string) => void;
  record: () => void;
};

/**
 * Proxy the request object to a service registered in the
 * Autolyse system. This function is not intended for direct
 * use in any user-written code; instead, it's imported + used
 * in JS code generated from `.proto` files.
 *
 * @param {String} servicePath The service path
 * @param {Object} requestBody
 * @return {Promise}
 *
 * @example
 *
 *   makeRequest('/helloworld.Greeter/SayHello', {
 *     name: 'World',
 *   })
 *     .then((greeting) => {
 *       // Success
 *     })
 *     .catch(({ code, message }) => {
 *       // Failure
 *     })
 */
function makeRequest(
  servicePath: $TSFixMe,
  requestBody = {},
  options: $TSFixMe,
) {
  const interaction = createCustomerInteraction(
    trimServicePath(servicePath),
  ) as CustomerInteraction;
  return autolyseClient
    .post(ROOT + servicePath, JSON.stringify(requestBody), options)
    .then((response: $TSFixMe) => {
      // TODO:
      // Verify response header's content type before attempting to parse
      // JSON. We can short-circuit some microtasks + logic here and exit
      // with a standardized shape.
      if (response.ok) {
        interaction.success();
        interaction.record();
        return response.json();
      }
      return response.json().then((json: $TSFixMe) => {
        // We only want to parse the response if the status code is unauthorized.
        // Because services or middleware can return status codes for any reason, we
        // only want to check auth errors on specific cases. If the user is missing already,
        // it's safe to send the 403 reauth message.
        if (response.status < 500) {
          interaction.setDegraded(json.msg);
          interaction.success();
          interaction.record();

          if (response.status === 401 && json.meta?.details === '8p5e23b') {
            // Send a message to the channel created in keep-alive-poller
            postMessageHub.publish('reauth/403');
          }

          if (response.status === 403 && json.meta?.details === '3a321b3f') {
            postMessageHub.publish('reauth/403');
          }

          console.error(`Autolyse error: ${json.code} – ${json.msg}`);
          return Promise.reject(json);
        }

        // If the response is not a 4xx, we don't want to
        // set the interaction to degraded. We want to fail the interaction with
        // the error message from the service.
        console.error(`Autolyse error: ${json.code} – ${json.msg}`);
        interaction.fail(json.msg);
        interaction.record();
        return Promise.reject(json);
      });
    });
}

function trimServicePath(path: string): string {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  // Split into service and method
  const [service, method] = cleanPath.split('/');
  if (!service || !method) {
    return path;
  }
  // Get the last part after the last dot in the service
  const serviceName = service.substring(service.lastIndexOf('.') + 1);
  return `${serviceName}/${method}`;
}

/**
 * Updates the autolyse client's default set of headers to include
 * X-CRSF-Token.
 *
 * @param {String} token
 */
function setCSRFToken(token: $TSFixMe) {
  autolyseClient.setDefaultHeader('X-CSRF-Token', token);
  autolyseClient.setDefaultHeader('X-CSRF-Source', 'autolyse');
}

/**
 * Enables middleware to be added to autolyse client.
 *
 * @param {Function} middleware
 * @return {Function} a function that can be called to remove added middleware
 */
function addAutolyseClientMiddleware(middleware: $TSFixMe) {
  if (middleware && typeof middleware === 'function') {
    return autolyseClient.addMiddleware(middleware);
  }
}

export { makeRequest, setCSRFToken, addAutolyseClientMiddleware };
